import type { GsapRegistry } from '../../renderer/vfx/gsap-registry';
import type { ScreenFactory, ScreenHandle, ScreenId, ScreenRouter } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Factory options
// ─────────────────────────────────────────────────────────────────────────────

export interface RouterOpts {
  gsapRegistry: GsapRegistry;
  host: HTMLElement;
  registry: Partial<Record<ScreenId, ScreenFactory<unknown>>>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createScreenRouter(opts: RouterOpts): ScreenRouter {
  const { gsapRegistry, host, registry } = opts;

  let _current: ScreenId | null = null;
  let _handle: ScreenHandle | null = null;
  const _overlays: Array<{ id: ScreenId; handle: ScreenHandle }> = [];
  let _disposed = false;
  let _navigating = false;

  function mount<P = unknown>(id: ScreenId, payload?: P): ScreenHandle {
    const factory = registry[id] as ScreenFactory<P> | undefined;
    if (factory == null) {
      throw new Error(`No factory registered for screen: ${id}`);
    }

    return factory({
      gsapRegistry,
      host,
      router,
      payload: payload as P,
    });
  }

  function closeOverlayAt(index: number): void {
    const [overlay] = _overlays.splice(index, 1);
    overlay?.handle.dispose();
  }

  function closeAllOverlays(): void {
    while (_overlays.length > 0) {
      closeOverlayAt(_overlays.length - 1);
    }
  }

  const router: ScreenRouter = {
    get current(): ScreenId | null {
      return _current;
    },

    get overlayCurrent(): ScreenId | null {
      return _overlays.at(-1)?.id ?? null;
    },

    navigate<P = unknown>(id: ScreenId, payload?: P): void {
      if (_navigating) {
        throw new Error('navigate() called re-entrantly');
      }

      _navigating = true;
      try {
        closeAllOverlays();
        // Dispose the prior screen handle (if any).
        _handle?.dispose();
        _handle = null;
        _current = null;

        // Mount the new screen.
        _handle = mount(id, payload);
        _current = id;
      } finally {
        _navigating = false;
      }
    },

    clear(): void {
      if (_navigating) {
        throw new Error('clear() called during navigation');
      }

      _navigating = true;
      try {
        closeAllOverlays();
        _handle?.dispose();
        _handle = null;
        _current = null;
      } finally {
        _navigating = false;
      }
    },

    showOverlay<P = unknown>(id: ScreenId, payload?: P): void {
      if (_navigating) {
        throw new Error('showOverlay() called during navigation');
      }

      const existingIndex = _overlays.findIndex((overlay) => overlay.id === id);
      if (existingIndex >= 0) {
        closeOverlayAt(existingIndex);
      }
      _overlays.push({ id, handle: mount(id, payload) });
    },

    closeOverlay(id?: ScreenId): void {
      if (id == null) {
        if (_overlays.length > 0) closeOverlayAt(_overlays.length - 1);
        return;
      }
      for (let index = _overlays.length - 1; index >= 0; index -= 1) {
        if (_overlays[index]?.id === id) {
          closeOverlayAt(index);
          return;
        }
      }
    },

    dispose(): void {
      if (_disposed) {
        // Idempotent: second call is a no-op.
        return;
      }
      _disposed = true;
      closeAllOverlays();
      _handle?.dispose();
      _handle = null;
      _current = null;
    },
  };

  return router;
}
